import { useEffect, useState } from "react";

// User-pinned favorites — one list per entity kind. localStorage-backed for
// v1 (per-device); a future server-sync would mirror this same shape onto
// UserDisplay or a new `favorites` table. The mirror was deliberate: same
// pattern as recent-searches so the two can share storage idioms.

export type FavoriteKind = "policy" | "agency" | "claim";

export interface Favorite {
  kind: FavoriteKind;
  number: string;
  name: string | null;
  // Pinned-at timestamp drives stable list ordering (newest first); we
  // don't auto-evict like recent-searches because the user owns this list.
  ts: number;
}

const STORAGE_KEY = "memic-favorites";
const CHANGE_EVENT = "memic-favorites-change";

function readStorage(): Favorite[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const out: Favorite[] = [];
    for (const item of parsed) {
      if (
        item &&
        typeof item === "object" &&
        "kind" in item &&
        "number" in item &&
        "ts" in item &&
        (item.kind === "policy" || item.kind === "agency" || item.kind === "claim") &&
        typeof item.number === "string" &&
        typeof item.ts === "number"
      ) {
        const name =
          "name" in item && (typeof item.name === "string" || item.name === null)
            ? item.name
            : null;
        out.push({ kind: item.kind, number: item.number, name, ts: item.ts });
      }
    }
    return out;
  } catch {
    return [];
  }
}

function writeStorage(items: Favorite[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    // Quota / storage disabled. Favorites are a UX layer; degrade silently.
  }
}

export function addFavorite(entry: Omit<Favorite, "ts">): void {
  const existing = readStorage();
  const filtered = existing.filter(
    (item) => !(item.kind === entry.kind && item.number === entry.number),
  );
  writeStorage([{ ...entry, ts: Date.now() }, ...filtered]);
}

export function removeFavorite(kind: FavoriteKind, number: string): void {
  const existing = readStorage();
  writeStorage(existing.filter((item) => !(item.kind === kind && item.number === number)));
}

export function isFavorited(kind: FavoriteKind, number: string): boolean {
  return readStorage().some((item) => item.kind === kind && item.number === number);
}

// Subscribe to the entire list. Components that just want "is this one
// pinned?" can call useIsFavorited(kind, number) — it reuses this hook
// internally so a single localStorage read per change covers both cases.
export function useFavorites(): Favorite[] {
  const [items, setItems] = useState<Favorite[]>(() => readStorage());

  useEffect(() => {
    const refresh = () => setItems(readStorage());
    window.addEventListener(CHANGE_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  return items;
}

export function useIsFavorited(kind: FavoriteKind, number: string | null | undefined): boolean {
  const items = useFavorites();
  if (!number) return false;
  return items.some((item) => item.kind === kind && item.number === number);
}
